import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";

export default function CariVideo() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  
  // State untuk menyimpan ID kategori yang dipilih
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  
  // State untuk list kategori dari API
  const [categories, setCategories] = useState([]);

  // 1. Fetch Categories dari API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("/kategori");
        setCategories(res.data);
      } catch (err) {
        console.error("Gagal mengambil kategori:", err);
      }
    };
    fetchCategories();
  }, []);

  // 2. Fetch Videos dan Filter
  useEffect(() => {
    // console.log("--- Fetch Videos Dijalankan ---");
    const fetchVideos = async () => {
      try {
        setLoading(true);
        // Always get all videos first (ideally filtering should be server-side, but keeping client-side for consistency)
        const response = await api.get("/youtube/videos");
        let filteredVideos = response.data || [];

        // LOGIKA SORTING (Tetap sama)
        filteredVideos.sort((a, b) => {
          const dateA = a.publishedAt ? new Date(a.publishedAt) : null;
          const dateB = b.publishedAt ? new Date(b.publishedAt) : null;
          const isValidA = dateA && !isNaN(dateA.getTime());
          const isValidB = dateB && !isNaN(dateB.getTime());

          if (isValidA && !isValidB) return -1;
          if (!isValidA && isValidB) return 1;
          if (isValidA && isValidB) {
            return dateB - dateA;
          }
          const createdA = new Date(a.CreatedAt || a.created_at || 0);
          const createdB = new Date(b.CreatedAt || b.created_at || 0);
          return createdB - createdA;
        });

        // Filter by search term
        if (searchTerm.trim()) {
          filteredVideos = filteredVideos.filter((video) =>
            video.title.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        // Filter by category ID
        if (selectedCategoryId) {
          filteredVideos = filteredVideos.filter(
            (video) => 
              // Convert selectedCategoryId to number for comparison
              (video.category?.ID === parseInt(selectedCategoryId)) || 
              (video.category_id === parseInt(selectedCategoryId))
          );  
        }

        setVideos(filteredVideos);

        if (!userLoading) {
          logUserActivity(
            "user",
            "view_videos",
            "CariVideo",
            `User viewed videos with search term: ${searchTerm}, category ID: ${selectedCategoryId}`
          );
        }
      } catch (error) {
        console.error("Failed to fetch videos:", error);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [searchTerm, selectedCategoryId, userLoading]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 hari yang lalu";
    if (diffDays < 30) return `${diffDays} hari yang lalu`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} bulan yang lalu`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} tahun yang lalu`;
  };

  // Format duration helper
  const formatDuration = (duration) => {
    if (!duration) return "-";
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (match) {
      const hours = parseInt(match[1]) || 0;
      const minutes = parseInt(match[2]) || 0;
      const seconds = parseInt(match[3]) || 0;

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      } else {
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }
    }
    return duration;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategoryId(e.target.value);
  };

  // Helper untuk mendapatkan nama kategori dari ID untuk display chip
  const getCategoryNameById = (id) => {
    const cat = categories.find(c => c.ID === parseInt(id));
    return cat ? cat.nama_kategori : "Unknown";
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_video_search",
              "CariVideo",
              "User clicked back from video search"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Video</h1>
        <button>
          {/* <img src="/icon-bookmark.png" alt="Bookmark" className="h-6 w-6" /> */}
        </button>
      </div>

      <div className="flex items-center space-x-3 mb-4">
        {/* Search Bar */}
        <div className="relative flex-[2]">
          <img
            src="/icon-search.png"
            alt="Search"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
          />
          <input
            type="text"
            placeholder="Masukkan kata kunci..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm"
          />
        </div>

        {/* Category Filter Dropdown */}
        <div className="relative flex-1">
          <select
            value={selectedCategoryId}
            onChange={handleCategoryChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Semua Kategori</option>
            {categories.map((category) => (
              <option key={category.ID} value={category.ID}>
                {category.nama_kategori}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(searchTerm || selectedCategoryId) && (
        <div className="mb-4 flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              Pencarian: "{searchTerm}"
              <button
                onClick={() => setSearchTerm("")}
                className="text-blue-600 hover:text-blue-800"
              >
                x
              </button>
            </span>
          )}
          {selectedCategoryId && (
            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
              Kategori: {getCategoryNameById(selectedCategoryId)}
              <button
                onClick={() => setSelectedCategoryId("")}
                className="text-green-600 hover:text-green-800"
              >
                x
              </button>
            </span>
          )}
        </div>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Memuat video...</div>
      )}

      {/* Video List */}
      {!loading && (
        <div className="space-y-4">
          {videos.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              {searchTerm ? "Tidak ada video ditemukan." : "Belum ada video."}
            </div>
          ) : (
            videos.map((video) => (
              <button
                key={video.id || video.ID}
                onClick={() => navigate(`/video/${video.id || video.ID}`)}
                className="flex gap-3 w-full text-left hover:bg-gray-50 p-1 rounded-lg transition min-w-0"
              >
                <div className="relative w-40 shrink-0">
                  <img
                    src={video.thumbnail || "/cari-video1.png"}
                    alt={video.title}
                    className="w-full rounded-lg"
                    onError={(e) => {
                      e.target.src = "/cari-video1.png";
                    }}
                  />
                  <span className="absolute bottom-1 right-1 text-xs bg-black/70 text-white px-1 rounded">
                    {formatDuration(video.durasi || video.duration)}
                  </span>
                </div>
                <div className="text-sm flex-1">
                  <p className="font-medium mb-1 line-clamp-2">{video.title}</p>
                  <p className="text-gray-500">
                    {video.channelTitle || video.sumber || "Unknown Source"}
                  </p>
                  <p className="text-gray-400">
                    {formatDate(video.publishedAt)}
                  </p>
                  
                  {/* Tampilkan Nama Kategori dari Relasi */}
                  {video.category && (
                    <p className="text-xs text-blue-600 bg-blue-50 inline-block px-2 py-1 rounded mt-1">
                      {video.category.nama_kategori}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}